import React from "react";
import htmlParse from "html-react-parser";
import Button from "../../../../Foundation/Button/Button";
import { _LandingHighlighProductPetitPrinceRoot } from "./LandingHighlighProductPetitPrince.styled";
import Typography from "../../../../Foundation/Typography/Typography";
import LandingSectionBase, {
  ILandingSectionBaseProps,
} from "../../LandingSectionBase/LandingSectionBase";
import FlexBox from "../../../FlexBox/FlexBox";
import CompositionItem from "../CompositionItem/CompositionItem";
import { ICompositionVariant } from "../LandingHighlighProduct";
import { ImageFit } from "../../../Image/interface/ImageData";
import ImageResponsive from "../../../Image/ImageResponsive";
import PetitPrincePriceList from "./PetitPrincePriceList/PetitPrincePriceList";
import StyledDiv from "../../../StyledDiv/StyledDiv";
import { defaultTheme } from "../../../../Themes/defaultTheme";
import LandingHighlighProductPetitPrinceDecorationBase from "./Decorations/LandingHighlighProductPetitPrinceDecorationBase/LandingHighlighProductPetitPrinceDecorationBase";
import FadeIn from "../../../Animations/FadeIn";

export interface ILandingHighlighProductPetitPrinceProps {
  image?: string;
  video?: string;
  animation?: ILandingSectionBaseProps["animation"];
  title: string;
  subtitle?: string;
  description?: string;
  ctaLabel: string;
  onClick?: () => void;
  backgroundImage?: {
    desktop?: string;
    mobile?: string;
  };
  imageLeft?: boolean;
  compositions?: ICompositionVariant;
  compositionLabel: string;
  price?: string;
  priceSubtitle?: string;
  product_display_from_mention?: string;
  product_price_vae?: string;
  product_price_vad?: string;
  vaeLabel?: string;
  vadLabel?: string;
  variant?: "top-center-title" | "composition-title";
  mobileAspectRatio?: number;
}

const LandingHighlighProductPetitPrince = React.memo(
  function LandingHighlighProductPetitPrince(
    props: ILandingHighlighProductPetitPrinceProps
  ) {
    const {
      image,
      title,
      subtitle,
      description,
      ctaLabel = "Je découvre",
      onClick,
      backgroundImage,
      imageLeft = false,
      compositionLabel,
      compositions,
      price,
      priceSubtitle,
      product_display_from_mention,
      product_price_vae,
      product_price_vad,
      variant = "composition-title",
      vaeLabel,
      vadLabel,
      mobileAspectRatio = 413 / 435,
    } = props;
    const isTopTitle = variant === "top-center-title";
    const topContent = (
      <StyledDiv
        as={FlexBox}
        direction="column"
        gap={8}
        className="top-container"
        styles={{ maxWidth: 425 }}
      >
        <FadeIn delay={0.1} start="bottom bottom">
          <Typography color="petitPrinceHighlightTitle" variant="petitPrince1">
            {title}
          </Typography>
        </FadeIn>
        {subtitle && (
          <FadeIn delay={0.2} start="bottom bottom">
            <Typography
              color="petitPrinceHighlightTitle"
              variant="h6"
              style={{ maxWidth: 416 }}
            >
              {htmlParse(subtitle + "")}
            </Typography>
          </FadeIn>
        )}
        {description && (
          <FadeIn delay={0.25} start="bottom bottom">
            <StyledDiv
              as={Typography}
              color="petitPrinceHighlightTitle"
              variant="body2"
              styles={{
                display: "inline-block",
                marginTop: 17 - 8,
                mobile: {
                  display: "none",
                },
              }}
            >
              {htmlParse(description + "")}
            </StyledDiv>
          </FadeIn>
        )}
      </StyledDiv>
    );

    const desktopAspectRatio = 348 / 367;

    return (
      <_LandingHighlighProductPetitPrinceRoot>
        <StyledDiv
          styles={{
            position: "absolute",
            zIndex: 1,
            width: "100%",
            height: "100%",
            right: 0,
            bottom: 0,
            translate: "39% 10%",
            mobile: {
              scale: "0.5",
              translate: "-42% 22%",
            },
          }}
        >
          <LandingHighlighProductPetitPrinceDecorationBase />
        </StyledDiv>
        <LandingSectionBase
          style={{
            position: "relative",
            zIndex: 2,
          }}
          className="landing-section-base-petit-prince"
          boxContainer={true}
          topVariant="align-left"
          topPosition={(isTopTitle && "top") || undefined}
          topContentSize={"default"}
          backgroundImage={backgroundImage}
          imageAspectRatio={desktopAspectRatio}
          mobileMinHeight="129vw"
          renderImage={
            <StyledDiv
              as="span"
              className="landing-highligh-product-petit-prince-image"
              styles={{
                display: "inline-block",
                mobile: {
                  display: "block",
                  width: "100%",
                  aspectRatio: mobileAspectRatio,
                  scale: "1.36",
                  translate: "11% -10%",
                },
              }}
            >
              <ImageResponsive
                {...{
                  alt: "",
                  desktop: {
                    image: {
                      id: image,
                      fit: ImageFit.COVER,
                      quality: 80,
                      aspectRatio: desktopAspectRatio,
                      width: 904,
                    },
                    imgStyle: {
                      borderRadius: 4,
                      overflow: "hidden",
                      aspectRatio: desktopAspectRatio,
                      maxWidth: 904,
                    },
                  },
                  mobile: {
                    image: {
                      id: image,
                      fit: ImageFit.COVER,
                      quality: 80,
                      aspectRatio: mobileAspectRatio,
                      outputPixelDensities: [1, 2],
                      width: 711,
                    },
                    imgStyle: {
                      borderRadius: 4,
                      overflow: "hidden",
                      aspectRatio: mobileAspectRatio,
                      maxWidth: 711,
                    },
                  },
                }}
              />
            </StyledDiv>
          }
          imageLeft={imageLeft}
          bottomContent={
            <>
              {/* top content */}
              {topContent}

              {/* compo content */}
              <FlexBox
                direction="column"
                gap={26}
                style={{ marginTop: 44 - 9 }}
              >
                <FadeIn delay={0.3}>
                  <StyledDiv
                    as={FlexBox}
                    mt={32}
                    direction="column"
                    gap={17}
                    className="composition-container"
                  >
                    {/* <Typography variant="ui">{compositionLabel}</Typography> */}
                    {!compositions ? null : compositions.type === "box" ? (
                      <StyledDiv
                        as={FlexBox}
                        direction="row"
                        gap={41}
                        className="composition-items"
                        styles={{
                          mobile: {
                            flexDirection: "column",
                            gap: 8,
                          },
                        }}
                      >
                        {/* Highlighted items - left column on desktop, top on mobile */}
                        <FlexBox
                          direction="column"
                          gap={2}
                          className="composition-items-highlighted"
                        >
                          {compositions.list
                            .filter((item) => item.highlighted)
                            .map((item) => (
                              <CompositionItem
                                key={item.id_product}
                                product={item}
                                theme={"petit-prince"}
                                textColor={"white"}
                              />
                            ))}
                        </FlexBox>
                        {/* Non-highlighted items - right column on desktop, bottom on mobile */}
                        <FlexBox
                          direction="column"
                          gap={2}
                          className="composition-items-regular"
                        >
                          {compositions.list
                            .filter((item) => !item.highlighted)
                            .map((item) => (
                              <CompositionItem
                                key={item.id_product}
                                product={item}
                                theme={"petit-prince"}
                                textColor={"white"}
                              />
                            ))}
                        </FlexBox>
                      </StyledDiv>
                    ) : compositions.type === "paragraph" ? (
                      <Typography>{compositions.text}</Typography>
                    ) : null}
                  </StyledDiv>
                </FadeIn>

                {/* button */}
                <FlexBox direction="column" gap={24}>
                  <FadeIn delay={0.1} start="top bottom">
                    <PetitPrincePriceList
                      price={price}
                      product_price_vae={product_price_vae}
                      product_price_vad={product_price_vad}
                      product_display_from_mention={
                        product_display_from_mention
                      }
                      priceSubtitle={priceSubtitle}
                      vaeLabel={vaeLabel}
                      vadLabel={vadLabel}
                    />
                  </FadeIn>
                  <div>
                    <FadeIn delay={0.1} start="top bottom">
                      <Button
                        customize={{
                          color: defaultTheme.color.petitPrinceTextBlue,
                          bg: defaultTheme.color.petitPrinceHighlightTitle,
                          border: defaultTheme.color.petitPrinceHighlightTitle,
                        }}
                        className="landing-action-button"
                        onClick={onClick}
                        size="extra-large"
                        style={{
                          minHeight: 46,
                        }}
                      >
                        {ctaLabel}
                      </Button>
                    </FadeIn>
                  </div>
                </FlexBox>
              </FlexBox>
            </>
          }
          onImageClick={onClick}
        />
      </_LandingHighlighProductPetitPrinceRoot>
    );
  }
);

export default LandingHighlighProductPetitPrince;
